import { ParentSize } from '@visx/responsive'
import { Carousel } from 'antd'
import { useState, type FunctionComponent } from 'react'
import styled from 'styled-components'
import { useDeleteCollaborationRecordScoringMutation } from '@/api/workspace-api/directory-api'
import EditScoringModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/edit-scoring-modal'
import ScoringView, {
  type ScoringViewProps,
} from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/scoring-view'
import useNotification from '@/hooks/antd/use-notification'
import { useSelector } from '@/hooks/use-selector'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  CollaborationRecordScoringType,
  Scoring,
} from '@/types/schema/directory/collaboration-record'
import { ampli } from '@/utils/ampli'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import logger from '@/utils/logger'

interface ScoringListProps {
  data: Scoring[]
  kolId: string
  kolName: string
  collaborationRecordId: number
  collaborationRecordName: string
  collaborationRecordScoringType: CollaborationRecordScoringType
}

const ScoringList: FunctionComponent<ScoringListProps> = ({
  data,
  kolId,
  kolName,
  collaborationRecordId,
  collaborationRecordName,
  collaborationRecordScoringType,
}) => {
  const notification = useNotification()
  const { formatMessage } = useIntl()

  const {
    data: { at: from },
  } = useSelector((state) => state.kolCooperationDetailDrawer)

  const [editingScoring, setEditingScoring] = useState<Scoring>()

  const [deleteCollaborationRecordScoring] =
    useDeleteCollaborationRecordScoringMutation()

  const handleDelete: ScoringViewProps['onDeleteScoring'] = async (
    scoring,
  ): Promise<void> => {
    try {
      await deleteCollaborationRecordScoring({
        kolId,
        collaborationRecordScoringId: scoring.id,
        collaborationRecordScoringType,
      }).unwrap()

      const sharedEventProperties = {
        kolId,
        kolName,
        action: 'Delete',
        feedbackId: scoring.id,
        from,
        rating: scoring.evaluation_score,
        fieldValue: scoring.evaluation_content,
      } as const

      switch (collaborationRecordScoringType) {
        case CollaborationRecordScoringType.CampaignKol:
          ampli.configureFeedbackForCampaign({
            ...sharedEventProperties,
            campaignId: collaborationRecordId,
            campaignName: collaborationRecordName,
          })
          break
        case CollaborationRecordScoringType.ReportKol:
          ampli.configureFeedbackForDeepReport({
            ...sharedEventProperties,
            reportId: collaborationRecordId,
            reportName: collaborationRecordName,
          })
          break
        case CollaborationRecordScoringType.Customized:
          ampli.configureFeedbackForCustomCollaboration({
            ...sharedEventProperties,
            collaborationId: collaborationRecordId,
            collaborationName: collaborationRecordName,
          })
          break
        default:
          exhaustiveCheck(collaborationRecordScoringType)
      }

      notification.success({
        message: formatMessage({
          id: 'general:operation_success',
        }),
        placement: 'bottomRight',
      })
    } catch (error) {
      logger.error(error)
      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <>
      <ParentSize>
        {({ width }) => (
          <Container $width={width}>
            <Carousel draggable>
              {data.map((scoring) => (
                <ScoringView
                  key={scoring.id}
                  scoring={scoring}
                  onClickEditScoring={setEditingScoring}
                  onDeleteScoring={handleDelete}
                />
              ))}
            </Carousel>
          </Container>
        )}
      </ParentSize>
      {editingScoring && (
        <EditScoringModal
          open
          collaborationRecordId={collaborationRecordId}
          collaborationRecordName={collaborationRecordName}
          collaborationRecordScoringType={collaborationRecordScoringType}
          kolId={kolId}
          kolName={kolName}
          scoring={editingScoring}
          onClose={() => setEditingScoring(undefined)}
        />
      )}
    </>
  )
}

const Container = styled.div<{ $width: number }>`
  width: ${({ $width }): string => `${$width}px`};

  .ant-carousel {
    --ant-carousel-dot-height: 8px;
    --ant-carousel-dot-width: 8px;
    --ant-carousel-dot-active-width: 8px;

    .slick-slider {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .slick-track {
      display: flex;
      align-items: center;
    }

    .slick-dots-bottom {
      bottom: 0;
    }

    .slick-dots {
      position: relative;
    }

    li button {
      background: ${({ theme }): string => theme.colors.brand.secondary};
    }

    li.slick-active button {
      background: ${({ theme }): string => theme.colors.brand.primary};
    }
  }
`

export default ScoringList
